<script setup lang="ts">
import type { IconNode } from 'lucide';
import type { IconNode as IconNodeNext } from 'lucide-vue-next';
import {
    ArrowDownAZ,
    ArrowRight,
    ArrowRightLeft,
    ArrowUpAZ,
    Check,
    ChevronDown,
    ChevronLeft,
    Facebook,
    Key,
    Lightbulb,
    ListFilter,
    LogIn,
    Pencil,
    PenLine,
    Rabbit,
    RefreshCw,
    Repeat,
    Send,
    SquarePen,
    Wand,
} from 'lucide';
import { createLucideIcon } from 'lucide-vue-next';
import { computed, toRaw } from 'vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<IconProps>(), {
    size: 'md',
    ariaLabel: undefined,
    svgAttributes: undefined,
    ai: false,
});

export type IconSize = 'inherit' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/** A Lucide icon, or the markup of an SVG file as an `import` of it with `?raw` gives it. */
export type IconSource = IconNode | string;

export interface IconProps {
    /**
     * Icon data to render as SVG icon, or the markup of an SVG file. A file is drawn as it is, only its fill and
     * stroke colours are taken over by the icon's colour; `svgAttributes` and the stroke width don't reach it.
     * Markup is inserted as HTML, so it may only come from the project's own files, never from user input.
     */
    icon: IconSource
    /**
     * Size of the icon. `inherit` binds it to the surrounding font size instead of a fixed step.
     * Can also be defined via CSS variable `--sph-icon-size`.
     * Default: 'md'
     */
    size?: IconSize
    /**
     * Human-readable label for assistive technologies when the icon is meaningful.
     * When provided, the icon will be exposed with role="img".
     */
    ariaLabel?: string

    svgAttributes?: Record<string, string>
    /**
     * Marks the icon as AI feature: sparks in the secondary brand color, cut out of the icon. They sit in
     * the corner that hides the least of the icon, see `sparksPositions`. Hidden at size `xs`, too small for them.
     */
    ai?: boolean
}

interface Point {
    x: number
    y: number
}

/**
 * Center of the large spark in the icon's 24×24 viewBox. The small one sits next to it towards the nearest
 * corner, unless `small` places it elsewhere.
 */
interface SparksPosition extends Point {
    small?: Point
}

const SPARKS_TOP_RIGHT: SparksPosition = { x: 18.5, y: 5.5 };
const SPARKS_TOP_LEFT: SparksPosition = { x: 5.5, y: 5.5 };
const SPARKS_BOTTOM_RIGHT: SparksPosition = { x: 18.5, y: 18.5 };
const SPARKS_BOTTOM_LEFT: SparksPosition = { x: 5.5, y: 18.5 };

const SMALL_SPARK_TOP_RIGHT: Point = { x: 22.4, y: 1.6 };
const SMALL_SPARK_TOP_LEFT: Point = { x: 1.6, y: 1.6 };

/* Icons whose strokes run through the default top right corner. An entry is worth it once another corner
   hides at least 10 percentage points less of the icon's strokes (Pencil: 38% top right, 0% top left).
   Icons with room in two opposite corners spread the sparks across them. */
const sparksPositions = new Map<IconSource, SparksPosition>([
    [ArrowDownAZ, SPARKS_TOP_LEFT],
    [ArrowRight, { ...SPARKS_TOP_LEFT, small: SMALL_SPARK_TOP_RIGHT }],
    [ArrowRightLeft, SPARKS_BOTTOM_RIGHT],
    [ArrowUpAZ, SPARKS_BOTTOM_LEFT],
    [Check, { ...SPARKS_TOP_LEFT, small: SMALL_SPARK_TOP_RIGHT }],
    [ChevronDown, SPARKS_BOTTOM_RIGHT],
    [ChevronLeft, SPARKS_TOP_LEFT],
    [Facebook, SPARKS_TOP_LEFT],
    [Key, { ...SPARKS_TOP_LEFT, small: { x: 18.5, y: 17.5 } }],
    [Lightbulb, { ...SPARKS_BOTTOM_LEFT, small: SMALL_SPARK_TOP_RIGHT }],
    [ListFilter, SPARKS_BOTTOM_RIGHT],
    [LogIn, SPARKS_BOTTOM_LEFT],
    [Pencil, { ...SPARKS_TOP_LEFT, small: { x: 20.5, y: 20.5 } }],
    [PenLine, SPARKS_TOP_LEFT],
    [Rabbit, SPARKS_TOP_LEFT],
    [RefreshCw, SPARKS_TOP_LEFT],
    [Repeat, SPARKS_TOP_LEFT],
    [Send, { ...SPARKS_BOTTOM_LEFT, small: SMALL_SPARK_TOP_LEFT }],
    [SquarePen, SPARKS_TOP_LEFT],
    [Wand, { x: 4.5, y: 4.5, small: { x: 19.5, y: 19.5 } }],
]);

/* Small icons draw the sparks with only a few pixels; they grow towards the icon's center to stay legible. */
const sparksScales: Partial<Record<IconSize, number>> = {
    inherit: 1.3,
    sm: 1.15,
};

const SPARK_RADIUS = 3.7;
const SMALL_SPARK_RADIUS = 2.3;
const SMALL_SPARK_OFFSET = 3.9;
/* Spark outline plus the gap to the icon, in viewBox units: wide enough to clear the thickest stroke. */
const SPARKS_KNOCKOUT_WIDTH = 4;

function sparkPath(cx: number, cy: number, r: number): string {
    const k = r * 0.12;
    const p = (x: number, y: number) => `${+x.toFixed(2)} ${+y.toFixed(2)}`;

    return `M${p(cx, cy - r)}Q${p(cx + k, cy - k)} ${p(cx + r, cy)}Q${p(cx + k, cy + k)} ${p(cx, cy + r)}`
        + `Q${p(cx - k, cy + k)} ${p(cx - r, cy)}Q${p(cx - k, cy - k)} ${p(cx, cy - r)}Z`;
}

const sparks = computed(() => {
    if (!props.ai || props.size === 'xs') {
        return undefined;
    }

    const position = sparksPositions.get(toRaw(props.icon)) ?? SPARKS_TOP_RIGHT;
    const scale = sparksScales[props.size] ?? 1;
    const towardsCenter = (point: Point, radius: number): Point => {
        const growth = radius * (scale - 1);

        return {
            x: point.x + Math.sign(12 - point.x) * growth,
            y: point.y + Math.sign(12 - point.y) * growth,
        };
    };

    const largeCenter = towardsCenter(position, SPARK_RADIUS);
    const smallOffset = SMALL_SPARK_OFFSET * scale;
    const smallCenter = position.small
        ? towardsCenter(position.small, SMALL_SPARK_RADIUS)
        : {
                x: largeCenter.x + (position.x >= 12 ? smallOffset : -smallOffset),
                y: largeCenter.y + (position.y <= 12 ? -smallOffset : smallOffset),
            };

    const large = sparkPath(largeCenter.x, largeCenter.y, SPARK_RADIUS * scale);
    const small = sparkPath(smallCenter.x, smallCenter.y, SMALL_SPARK_RADIUS * scale);
    const knockout = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><mask id="k">'
        + '<rect x="-1" y="-1" width="26" height="26" fill="#fff"/>'
        + `<path d="${large}${small}" stroke="#000" stroke-width="${SPARKS_KNOCKOUT_WIDTH}" stroke-linejoin="round"/>`
        + '</mask><rect x="-1" y="-1" width="26" height="26" mask="url(#k)"/></svg>';

    return {
        large,
        small,
        mask: `url("data:image/svg+xml,${encodeURIComponent(knockout)}")`,
    };
});

const iconComponent = computed(() => {
    const data = toRaw(props.icon);
    if (!data || typeof data === 'string') {
        return undefined;
    }

    return createLucideIcon('icon', data as IconNodeNext)({}, {} as any);
});

const MASK = /(<mask\b[\s\S]*?<\/mask>)/;
const PAINT = /\b(fill|stroke)="(?!none"|url\()[^"]*"/g;

// Every colour the file was exported with gives way to the icon's. `none` and references such as `url(#mask)`
// stay, and so does a mask's content, whose black and white decide what shows.
function adoptIconColor(markup: string): string {
    return markup
        .split(MASK)
        .map((part) => (part.startsWith('<mask') ? part : part.replace(PAINT, '$1="currentColor"')))
        .join('');
}

const svgMarkup = computed(() => (typeof props.icon === 'string' ? adoptIconColor(props.icon) : undefined));
</script>

<template>
    <span
        class="sph-icon"
        :class="[props.size && `size-${props.size}`, sparks && 'is-ai']"
        :aria-label="props.ariaLabel || undefined"
        :role="props.ariaLabel ? 'img' : undefined"
        :aria-hidden="!props.ariaLabel ? 'true' : undefined"
    >
        <span
            v-if="svgMarkup !== undefined"
            class="sph-icon-markup"
            v-html="svgMarkup"
        ></span>
        <component
            :is="iconComponent"
            v-else
            v-bind="props.svgAttributes"
            :style="sparks && { maskImage: sparks.mask }"
        />
        <svg
            v-if="sparks"
            class="sph-icon-sparks"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path :d="sparks.large" />
            <path
                class="sph-icon-spark-small"
                :d="sparks.small"
            />
        </svg>
    </span>
</template>

<style>
@reference '@/assets/styles/index.css';

.sph-icon {
    --sph-icon-size: var(--sph-icon-size, calc(var(--spacing) * 6));

    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--sph-icon-size);
    height: var(--sph-icon-size);
    flex: none;
    line-height: 0;
    user-select: none;

    /* Only Lucide's svg: an SVG file sits one level deeper and keeps its own strokes and fills. */
    > svg {
        --stroke-width: var(--sph-icon-stroke-width, 1.375);

        display: block;
        width: 100%;
        height: 100%;
        stroke: var(--sph-icon-color, currentColor);
        fill: var(--sph-icon-fill, none);

        g,
        path,
        circle,
        rect,
        line,
        polyline,
        polygon,
        ellipse {
            stroke-width: var(--stroke-width);
        }
    }

    .sph-icon-markup {
        display: block;
        width: 100%;
        height: 100%;
        color: var(--sph-icon-color, currentColor);

        > svg {
            display: block;
            width: 100%;
            height: 100%;
        }
    }

    /* Binds the icon to the type it sits in, for an icon set inline with text: it scales with
       font-size rather than holding a fixed step, and lucide's artwork spans 18 of its 24 viewBox
       units, so 1em draws at 0.75em — a shade over cap height, which is where an inline icon
       belongs. The stroke matches a regular-weight stem at the same size. */
    /* The sparks take the secondary brand color on purpose, not the icon's: they have to stand out from
       it wherever the icon is colored. The icon is masked out around them (see `sparks.mask`). */
    &.is-ai {
        position: relative;

        > svg:first-child {
            mask-size: 100% 100%;
        }

        .sph-icon-sparks {
            position: absolute;
            inset: 0;
            overflow: visible;
            fill: var(--color-accent);
            stroke: var(--color-accent);
            stroke-linejoin: round;

            path {
                transform-box: fill-box;
                transform-origin: center;
                animation: sph-icon-spark-twinkle 3s ease-in-out infinite;

                @media (prefers-reduced-motion: reduce) {
                    animation: none;
                }
            }

            path.sph-icon-spark-small {
                --sph-icon-spark-twinkle-scale: 0.9;

                animation-delay: -1.5s;
            }
        }
    }

    &.size-inherit {
        --sph-icon-size: 1em;
        --sph-icon-stroke-width: 2;
    }

    /* equals: size-{x} */
    &.size-xs {
        --sph-icon-size: calc(var(--spacing) * 4);
        --sph-icon-stroke-width: 1.75;
    }
    &.size-sm {
        --sph-icon-size: calc(var(--spacing) * 5);
        --sph-icon-stroke-width: 1.5;
    }
    &.size-md {
        --sph-icon-size: calc(var(--spacing) * 6);
        --sph-icon-stroke-width: 1.375;
    }
    &.size-lg {
        --sph-icon-size: calc(var(--spacing) * 7);
        --sph-icon-stroke-width: 1.25;
    }
    &.size-xl {
        --sph-icon-size: calc(var(--spacing) * 8);
        --sph-icon-stroke-width: 1.125;
    }
    &.size-xxl {
        --sph-icon-size: calc(var(--spacing) * 9);
        --sph-icon-stroke-width: 1.125;
    }
}

/* Scales down only: the knockout around the sparks is cut for their full size. */
@keyframes sph-icon-spark-twinkle {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(var(--sph-icon-spark-twinkle-scale, 0.82));
        opacity: 0.8;
    }
}
</style>
